<template>
  <div
    v-if="items.length"
    :class="[
      `kpb-stats kpb-stats--theme-${theme} kpb-stats--width-${width} kpb-stats--variant-${variant} kpb-stats--transform-${transform} kpb-stats--position-${position}`,
      { 'kpb-stats--is-odd': items.length % 2 !== 0 },
    ]"
    :style="`--kpb-stats-padding: ${padding};`"
  >
    <div class="kpb-stats__content">
      <template v-for="(i, index) in items">
        <div class="kpb-stats__item" :key="index + 'stats-item'">
          <div class="kpb-stats__value">{{ i['show-as-text'] ? i.value : formatValue(i.value) }}</div>
          <div class="kpb-stats__label">
            {{ i.title }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
    variant: {
      type: String,
      default: 'solid',
      validator: val => ['solid', 'text-over', 'split'].includes(val),
      enums: ['solid', 'text-over', 'split', 'square'],
    },

    theme: {
      type: String,
      default: 'primary',
      validator(val) {
        return ['default', 'primary', 'secondary', 'dark', 'transparent'].includes(val)
      },
      enums: ['default', 'primary', 'secondary', 'dark', 'transparent'],
    },
    transform: {
      type: String,
      default: 'none',
      validator(val) {
        return ['none', 'vertical'].includes(val)
      },
      enums: ['none', 'vertical'],
    },

    width: {
      type: String,
      default: 'default',
      validator: val => ['default', 'full', 'medium'].includes(val),
      enums: ['default', 'full', 'medium'],
    },
    padding: {
      type: String,
      default: '2rem',
    },
    position: {
      type: String,
      default: 'center',
      validator: val => ['center', 'left', 'right'].includes(val),
      enums: ['center', 'left', 'right'],
    },
  },
  data() {
    return {}
  },
  computed: {
    items() {
      return this.$path('content', this.data) || []
    },
    isResposive() {
      return window.innerWidth < 992
    },
  },
  methods: {
    formatValue(value) {
      if (isNaN(value)) return value

      return parseInt(value).toLocaleString().replace(/,/g, ' ')
    },
  },
}
</script>

<style lang="scss">
.kpb-stats {
  $itemWidth: 300px;

  &--position {
    &-center {
      margin: 0 auto;
      text-align: center;
      .kpb-stats__item {
        align-items: center;
      }
    }
    &-left {
      left: 0;
      transform: none;
    }
    &-right {
      right: 0;
      transform: none;
    }
  }

  &__content {
    display: flex;
    padding: var(--kpb-stats-padding);
    position: relative;
    bottom: 0;

    justify-content: center;
    align-items: center;
    gap: 4rem;
    white-space: nowrap;

    @include respond-below('phone') {
      width: 100%;
      transform: none;
      left: initial;
      flex-wrap: wrap;
      flex-direction: row;
      gap: 2rem;
      align-items: start;
    }
  }

  &--transform {
    &-vertical {
      transform: translate(0, -50%);
    }
  }

  &--variant {
    &-text-over {
      @include respond-above('phone') {
        position: relative;
        left: 50%;
        transform: translate(-50%, 0%);
        margin: 0;

        .kpb-stats__item {
          transform: translateY(-30%);
        }

        .kpb-stats__content {
          padding: 0 var(--kpb-stats-padding);
        }
      }
    }
    &-solid {
      .kpb-stats__content {
        max-width: 1400px;
        margin: 0 auto;
      }
    }

    &-text-over,
    &-solid {
      .kpb-stats {
        &__value {
          font-size: clamp(1rem, 3vw, 7rem);
          font-weight: bold;

          &:after {
            content: '';
            margin-top: 0.5rem;
            margin-bottom: 0.75rem;
            height: 1px;
            background-color: var(--content-separator, black);
            width: calc(#{$itemWidth} / 2);

            @include respond-below('phone') {
              width: calc(#{$itemWidth} / 4);
            }
          }
        }

        &__label {
          font-size: 0.7rem;

          @include respond-below('phone') {
            font-size: 0.45rem;
            white-space: pre-wrap;
          }
        }
      }
    }

    &-split {
      .kpb-stats__content {
        gap: 0;
        @include respond-below('phone') {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          max-width: calc(100% - (2rem));
          margin: 0 auto;
        }
      }

      .kpb-stats {
        &__item {
          flex: 1;
          flex-direction: row-reverse;
          gap: 0.3em;
          padding: 0.6rem 1rem;
          width: 100%;

          &:not(:last-child) {
            border-right: solid 1px var(--content-separator, black);
          }

          @include respond-below('phone') {
            padding: 0;
            width: 100%;
            justify-content: start;

            &:nth-child(even) {
              padding-left: 1rem;
              border: none;
            }

            &:nth-child(odd) {
              border-right: solid 1px var(--content-separator, black);
            }
          }
        }
        &__label,
        &__value {
          font-size: clamp(0.2rem, 1vw, 1rem);
          @include respond-below('phone') {
            font-size: 0.625em;
          }
        }
      }

      @include respond-below('phone') {
        &.kpb-stats {
          &--is-odd {
            .kpb-stats__content {
              display: flex;
              flex-direction: column;
            }
            .kpb-stats {
              &__item {
                border: none;
                padding-left: 0;
                justify-content: center;
              }
            }
          }
        }
      }
    }

    &-square {
      white-space: nowrap;
      position: absolute;
      bottom: var(--kpb-stats-padding);
      right: var(--kpb-stats-padding);
      padding: 0;

      text-align: center;
      flex-wrap: wrap;
      gap: var(--kpb-stats-padding);
      flex-direction: row;

      .kpb-stats {
        &__item {
          background-color: var(--primary);
          border-radius: var(--border-radius);
          box-shadow: 0 10px 15px -10px rgba(0, 0, 0, 0.5%);
          color: var(--primary-contrast);
          line-height: 1;

          padding: 1.5rem 0.5rem;
          min-width: 130px;
          width: fit-content;
        }
        &__value {
          font-size: 2em;
          font-weight: 700;
          margin-bottom: 0.5rem;
        }
        &__label {
          font-size: 0.8em;
          opacity: 0.6;
        }
      }

      @include respond-below('phone') {
        position: static;
        flex-direction: row;
        gap: 0;

        width: 100%;
        .kpb-stats {
          &__item {
            border-radius: 0;
            padding: 1rem 0.5rem;
          }
          &__value {
            font-size: 1.25em;
          }
          &__label {
            font-size: 0.7em;
          }
        }
      }
    }
  }

  &--theme {
    &-default {
      background-color: white;
    }
    &-primary {
      background: var(--primary);
      color: var(--primary-contrast);
      --content-separator: var(--primary-contrast);
    }

    &-secondary {
      color: var(--secondary-contrast);
      background: var(--secondary);
      --content-separator: var(--secondary-contrast);
    }
    &-dark {
      color: white;
      background: black;
      --content-separator: white;
    }
    &-transparent {
      background: transparent;
    }
  }

  &--width {
    &-default {
      @include respond-below('phone') {
        width: 100%;
      }
    }
    &-full {
      width: 100%;
    }
    &-medium {
      max-width: 1400px;
      width: 100%;
    }
  }

  &__item {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: clamp(3vw, 20vw, 20vw);

    @include respond-below('phone') {
      width: calc(#{$itemWidth} / 4);
    }
  }

  &__value {
    position: relative;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
}
</style>
